<template>
  <div class="detail-page">
    <!-- Voltar -->
    <button class="detail-back" @click="$router.back()">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M19 12H5M12 5l-7 7 7 7"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      Voltar para Pacientes
    </button>

    <!-- Carregando -->
    <div v-if="loading" class="detail-loading">
      <b-spinner style="width:32px;height:32px;color:var(--color-primary)" />
    </div>

    <template v-else>
      <!-- Cabeçalho do paciente -->
      <div class="card detail-patient-card">
        <div class="detail-patient">
          <div class="avatar avatar--lg">
            <img
              v-if="patient.photo"
              :src="patient.photo"
              :alt="patient.name"
            />
            <span v-else>{{ initials(patient.name) }}</span>
          </div>
          <div class="detail-patient__info">
            <h1 class="detail-patient__name">{{ patient.name || "—" }}</h1>
            <div class="detail-patient__meta">
              <span v-if="patient.email" class="detail-patient__chip">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="2"
                    y="4"
                    width="20"
                    height="16"
                    rx="2"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <path
                    d="M2 7l10 7 10-7"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
                {{ patient.email }}
              </span>
              <span v-if="patient.phone_number" class="detail-patient__chip">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.12 21 3 13.88 3 5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{ patient.phone_number | phoneMask }}
              </span>
            </div>
          </div>
        </div>

        <!-- Data da consulta -->
        <div v-if="consultationDate" class="detail-patient__date-badge">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="3"
              y="4"
              width="18"
              height="17"
              rx="2"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M3 9h18M8 2v4M16 2v4"
              stroke="currentColor"
              stroke-width="2"
            />
          </svg>
          {{ consultationDate }}
        </div>
      </div>

      <!-- Observação -->
      <div class="card detail-section-card">
        <h2 class="detail-section-title">Observação da consulta</h2>
        <p v-if="observation" class="detail-observation">{{ observation }}</p>
        <p v-else class="detail-empty-text">Nenhuma observação registrada.</p>
      </div>

      <!-- Anotações clínicas -->
      <div class="card detail-section-card">
        <h2 class="detail-section-title">Anotações clínicas</h2>
        <PatientRecordNotesHistory
          v-if="appointmentId"
          :appointment-id="appointmentId"
        />
      </div>

      <!-- Documentos da consulta -->
      <div class="card detail-section-card">
        <h2 class="detail-section-title">Documentos da consulta</h2>

        <div v-if="documentsLoading" class="detail-docs-loading">
          <b-spinner
            style="width:20px;height:20px;color:var(--color-primary)"
          />
        </div>

        <template v-else>
          <!-- Receitas -->
          <div class="detail-doc-group">
            <h3 class="detail-doc-group__title">Receitas</h3>
            <div v-if="prescriptions.length === 0" class="detail-doc-empty">
              Nenhuma receita nesta consulta.
            </div>
            <div
              v-for="doc in prescriptions"
              :key="doc.id"
              class="detail-docfile"
            >
              <span class="detail-docfile__label">
                {{ prescriptionTitle(doc) }}
              </span>
              <span class="detail-docfile__date">
                {{ formatDate(doc.prescription_date || doc.created_at) }}
              </span>
              <button
                type="button"
                class="btn btn--ghost btn--sm"
                @click="openDocument(doc, 'receita')"
              >
                Ver
              </button>
              <button
                type="button"
                class="btn btn--ghost btn--sm"
                :data-test="'resend-' + doc.id"
                :disabled="resendingId === doc.id"
                @click="resendPrescription(doc)"
              >
                {{ resendingId === doc.id ? "Reenviando…" : "Reenviar" }}
              </button>
            </div>
          </div>

          <!-- Exames -->
          <div class="detail-doc-group">
            <h3 class="detail-doc-group__title">Exames</h3>
            <div v-if="examAttachments.length === 0" class="detail-doc-empty">
              Nenhum exame nesta consulta.
            </div>
            <div
              v-for="doc in examAttachments"
              :key="doc.id"
              class="detail-docfile"
            >
              <span class="detail-docfile__label">
                {{ doc.description || "Pedido de exame" }}
              </span>
              <span class="detail-docfile__date">
                {{ formatDate(doc.created_at) }}
              </span>
              <button
                v-if="doc.file_url"
                type="button"
                class="btn btn--ghost btn--sm"
                @click="openDocument(doc, 'exame')"
              >
                Ver
              </button>
            </div>
          </div>

          <!-- Atestados -->
          <div class="detail-doc-group">
            <h3 class="detail-doc-group__title">Atestados</h3>
            <div
              v-if="medicalCertificates.length === 0"
              class="detail-doc-empty"
            >
              Nenhum atestado nesta consulta.
            </div>
            <div
              v-for="doc in medicalCertificates"
              :key="doc.id"
              class="detail-docfile"
            >
              <span class="detail-docfile__label">
                {{ certificateTitle(doc) }}
              </span>
              <span class="detail-docfile__date">
                {{ formatDate(doc.issued_at || doc.created_at) }}
              </span>
              <button
                type="button"
                class="btn btn--ghost btn--sm"
                @click="openDocument(doc, 'atestado')"
              >
                Ver
              </button>
            </div>
          </div>
        </template>
      </div>
    </template>

    <document-view-modal ref="documentViewModal" />
  </div>
</template>

<script>
import { Appointments } from "../services/appointments";
import { MedicalCertificates } from "../services/medical_certificates";
import { Prescriptions } from "../services/prescriptions";
import { User } from "../services/user";
import PatientRecordNotesHistory from "../components/PatientRecordNotesHistory.vue";
import DocumentViewModal from "../components/DocumentViewModal.vue";

export default {
  name: "DoctorPatientDetailView",

  components: { PatientRecordNotesHistory, DocumentViewModal },

  filters: {
    phoneMask(value) {
      if (!value) return "—";
      const d = value.replace(/\D/g, "");
      if (d.length === 11)
        return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
      if (d.length === 10)
        return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return value;
    }
  },

  data() {
    return {
      loading: false,
      documentsLoading: false,
      patient: {},
      observation: null,
      consultationDate: null,
      prescriptions: [],
      examAttachments: [],
      medicalCertificates: [],
      resendingId: null
    };
  },

  mounted() {
    this.fetchData();
    this.fetchDocuments();
  },

  methods: {
    async fetchData() {
      if (!this.appointmentId || !this.patientId) return;
      this.loading = true;
      try {
        const [apptRes, userRes] = await Promise.all([
          Appointments.showAppointment({ id: this.appointmentId }),
          User.showSymptoms({ id: this.patientId })
        ]);

        const appt = apptRes.data || {};
        this.observation = appt.observation || null;
        if (appt.scheduled_at) {
          const d = new Date(appt.scheduled_at);
          this.consultationDate =
            d.toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric"
            }) +
            " às " +
            d.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit"
            });
        }

        const userData = (userRes.data || {}).user || {};
        this.patient = userData;
      } catch {
        this.patient = {};
      } finally {
        this.loading = false;
      }
    },

    async fetchDocuments() {
      if (!this.appointmentId) return;
      this.documentsLoading = true;
      try {
        const [prescRes, examRes, certRes] = await Promise.all([
          Prescriptions.fetchDoctorPrescriptions(this.appointmentId),
          Appointments.listAdminExamAttachments(this.appointmentId),
          MedicalCertificates.listAppointmentMedicalCertificates(
            this.appointmentId
          )
        ]);
        this.prescriptions = prescRes.data || [];
        this.examAttachments = examRes.data || [];
        this.medicalCertificates = certRes.data || [];
      } catch {
        this.prescriptions = [];
        this.examAttachments = [];
        this.medicalCertificates = [];
      } finally {
        this.documentsLoading = false;
      }
    },

    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("pt-BR");
    },

    prescriptionTitle(doc) {
      const names = (doc.drugs || []).map(d => d.name).filter(Boolean);
      return names.length ? names.join(", ") : "Memed · assinada digitalmente";
    },

    certificateTitle(doc) {
      return doc.description || "Atestado médico";
    },

    async resendPrescription(doc) {
      this.resendingId = doc.id;

      try {
        await Prescriptions.resendPrescription(doc.id);

        this.$bvToast.toast("Receita reenviada com sucesso.", {
          title: "Sucesso",
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        this.$bvToast.toast("Erro ao tentar reenviar a receita.", {
          title: "Erro",
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.resendingId = null;
      }
    },

    openDocument(doc, type) {
      const fullDoc = { ...doc, _type: type };
      this.$refs.documentViewModal.open(fullDoc, () =>
        this.refreshDocument(fullDoc)
      );
    },

    async refreshDocument(doc) {
      await this.fetchDocuments();
      const list =
        doc._type === "receita"
          ? this.prescriptions
          : doc._type === "exame"
          ? this.examAttachments
          : this.medicalCertificates;
      return list.find(d => d.id === doc.id);
    },

    initials(name) {
      return (
        (name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || "?"
      );
    }
  },

  computed: {
    appointmentId() {
      return this.$route.params.appointmentId;
    },
    patientId() {
      return this.$route.query.patientId;
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-page {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding: var(--space-6);
}

/* Voltar */
.detail-back {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  background: none;
  border: none;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: var(--color-primary);
  }
}

/* Carregando */
.detail-loading {
  display: flex;
  justify-content: center;
  padding: var(--space-16) 0;
}

/* Card paciente */
.detail-patient-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
  padding: var(--space-6);
}

.detail-patient {
  display: flex;
  align-items: center;
  gap: var(--space-5);

  &__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__name {
    font-size: var(--font-size-xl);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    margin: 0;
    line-height: 1.3;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-sm);
    color: var(--color-gray-600);

    svg {
      color: var(--color-gray-400);
      flex: none;
    }
  }

  &__date-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    background: rgba(26, 108, 247, 0.08);
    border-radius: var(--radius-full);
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-primary);
    white-space: nowrap;

    svg {
      color: var(--color-primary);
      flex: none;
    }
  }
}

/* Seções */
.detail-section-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6);
}

.detail-section-title {
  font-size: var(--font-size-base);
  font-weight: var(--fw-bold);
  color: var(--color-gray-800);
  margin: 0;
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--color-gray-100);
}

.detail-observation {
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
  line-height: 1.6;
  margin: 0;
  white-space: pre-line;
}

.detail-empty-text {
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
  margin: 0;
}

/* Documentos */
.detail-docs-loading {
  display: flex;
  justify-content: center;
  padding: var(--space-6) 0;
}

.detail-doc-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  & + & {
    margin-top: var(--space-5);
    padding-top: var(--space-5);
    border-top: 1px solid var(--color-gray-100);
  }

  &__title {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-600);
    margin: 0 0 var(--space-1);
  }
}

.detail-doc-empty {
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
}

.detail-docfile {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);

  &__label {
    flex: 1;
    font-size: var(--font-size-sm);
    color: var(--color-gray-800);
    font-weight: var(--fw-medium);
  }

  &__date {
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);
    white-space: nowrap;
  }
}

/* Avatar override para tamanho grande */
.avatar--lg {
  width: 64px;
  height: 64px;
  font-size: var(--font-size-xl);
}
</style>
